import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Sparkles } from "lucide-react";
import api from "@/lib/api";
import Particles from "@/components/Particles";
import Countdown from "@/components/Countdown";
import { useDataSaver } from "@/context/DataSaverContext";

export default function Coronation() {
  const { dataSaver } = useDataSaver();
  const [data, setData] = useState(null);
  const [revealed, setRevealed] = useState(0); // number of finalists revealed (from bottom)

  useEffect(() => { api.get("/coronation", { params: { top: 5 } }).then((r) => setData(r.data)).catch(() => {}); }, []);

  const finalists = data?.results || [];
  // reveal order: 5,4,3,2 then winner (1)
  const orderedForReveal = [...finalists].sort((a, b) => b.rank - a.rank);

  useEffect(() => {
    if (!finalists.length || dataSaver) { setRevealed(finalists.length); return; }
    if (revealed >= finalists.length) return;
    const t = setTimeout(() => setRevealed((r) => r + 1), revealed === 0 ? 800 : 1600);
    return () => clearTimeout(t);
  }, [finalists.length, revealed, dataSaver]);

  const winner = finalists.find((f) => f.rank === 1);
  const winnerRevealed = revealed >= finalists.length;

  return (
    <div className="relative min-h-screen overflow-hidden grain">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(230,0,92,0.18),transparent_65%)]" />
      <Particles count={40} />
      <div className="section relative z-10 py-16" data-testid="coronation-page">
        <div className="text-center">
          <Crown className="mx-auto mb-4 h-10 w-10 text-gold" />
          <p className="kicker mb-3">The Grand Coronation</p>
          <h1 className="text-4xl sm:text-6xl lg:text-7xl text-amber-50">The Final Five</h1>
          <p className="mt-4 mx-auto max-w-xl text-amber-100/60">
            Combined ranking · <span className="text-gold">{Math.round((data?.judge_weight ?? 0.5) * 100)}% judges</span> +{" "}
            <span className="text-gold">{Math.round((data?.vote_weight ?? 0.5) * 100)}% public vote</span>
          </p>
          {data?.voting_open && data?.finale_date && (
            <div className="mt-8 flex justify-center"><Countdown target={data.finale_date} /></div>
          )}
        </div>

        {/* Winner spotlight */}
        {winner && (
          <div className={`mx-auto mt-14 max-w-md transition-all duration-1000 ${winnerRevealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} data-testid="coronation-winner">
            <div className="relative card-lux overflow-hidden animate-pulseGold">
              <div className="aspect-[3/4] bg-obsidian-600">
                <img src={winner.portrait_url} alt={winner.name} className="h-full w-full object-cover object-top" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/20 to-transparent" />
              <div className="absolute left-0 right-0 top-4 text-center">
                <span className="inline-flex items-center gap-2 rounded-full gold-bg px-4 py-1.5 text-obsidian font-display font-bold uppercase tracking-wider text-xs"><Crown className="h-4 w-4" /> Reigning Winner</span>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-center">
                <span className="text-4xl">{winner.flag}</span>
                <h2 className="mt-2 font-serif text-4xl gold-text">{winner.name}</h2>
                <p className="text-sm text-amber-100/70">{winner.delegation_name}</p>
                <p className="mt-3 font-mono text-2xl text-amber-50">{winner.combined_score}<span className="text-sm text-muted-foreground"> pts</span></p>
                <Link to={`/candidates/${winner.slug}`} className="btn-ghost-gold mt-4 inline-block text-xs">View Profile</Link>
              </div>
            </div>
          </div>
        )}

        {/* Runners up */}
        <div className="mx-auto mt-12 grid max-w-4xl grid-cols-2 gap-4 sm:grid-cols-4">
          {finalists.filter((f) => f.rank > 1).map((f) => {
            const isShown = orderedForReveal.slice(0, revealed).some((r) => r.id === f.id);
            return (
              <Link key={f.id} to={`/candidates/${f.slug}`} data-testid={`coronation-rank-${f.rank}`}
                className={`card-lux relative transition-all duration-700 ${isShown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
                <span className="absolute left-2 top-2 z-10 gold-bg text-obsidian font-mono text-xs px-2 py-0.5 rounded-full">#{f.rank}</span>
                <div className="aspect-[3/4] overflow-hidden bg-obsidian-600">
                  <img src={f.portrait_url} alt={f.name} loading="lazy" className="h-full w-full object-cover object-top" />
                </div>
                <div className="p-3">
                  <p className="text-sm text-amber-50 flex items-center gap-1">{f.name} <span>{f.flag}</span></p>
                  <p className="font-mono text-xs gold-text">{f.combined_score} pts</p>
                </div>
              </Link>
            );
          })}
        </div>

        <p className="mt-10 text-center text-xs text-muted-foreground flex items-center justify-center gap-1"><Sparkles className="h-3 w-3" /> Scores combine confidential judge results and verified public votes. DEMO DATA.</p>
      </div>
    </div>
  );
}
